import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS, pinnedHeight, scrollToId, goToWaitlist } from "@/lib/site";
import { Wordmark } from "./Wordmark";
import { AnnouncementBar, useAnnouncement } from "./AnnouncementBar";

const useActiveSection = () => {
  const [active, setActive] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY + pinnedHeight() + 140;
        let cur = "home";
        let best = -Infinity;
        NAV_LINKS.forEach(({ id }) => {
          const el = document.getElementById(id);
          if (!el) return;
          const t = el.getBoundingClientRect().top + window.scrollY;
          if (t <= y && t > best) {
            best = t;
            cur = id;
          }
        });
        setActive(cur);
        setScrolled(window.scrollY > 12);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return { active, scrolled };
};

export const Nav = () => {
  const { active, scrolled } = useActiveSection();
  const [open, setOpen] = useState(false);
  const bar = useAnnouncement();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    requestAnimationFrame(() => requestAnimationFrame(() => scrollToId(id)));
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "border-[#1C2A3A]/80 bg-[#030507]/75" : "border-transparent bg-[#030507]/30"
      }`}
    >
      <AnnouncementBar shown={bar.shown} onDismiss={bar.dismiss} />
      <nav className="cl-container flex h-[72px] items-center gap-6" aria-label="Primary">
        <a href="#home" onClick={go("home")} data-testid="nav-logo" className="shrink-0 text-[15px] sm:text-base">
          <Wordmark testid="nav-wordmark" />
        </a>

        <ul className="ml-8 hidden items-center gap-6 whitespace-nowrap lg:flex xl:ml-12 xl:gap-9">
          {NAV_LINKS.map(({ id, label }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                onClick={go(id)}
                data-testid={`nav-link-${id}`}
                aria-current={active === id ? "true" : undefined}
                className={`group relative py-2 text-[11px] font-normal uppercase tracking-[0.2em] transition-colors duration-300 ${
                  active === id ? "text-white" : "text-[#9AAABD] hover:text-[#E6EDF4]"
                }`}
              >
                {label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-px w-[calc(100%-0.2em)] origin-left bg-[#E6EDF4] transition-transform duration-500 ${
                    active === id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-50"
                  }`}
                />
              </a>
            </li>
          ))}
        </ul>

        <div className="ml-auto flex items-center gap-3">
          <a href="#launching-soon" onClick={goToWaitlist} data-testid="nav-get-started-button" className="btn-gold btn-gold-sm hidden sm:inline-flex">
            Get Started
          </a>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#1C2A3A] text-[#E6EDF4] transition-colors hover:border-[#E7C99D]/60 lg:hidden"
          >
            {open ? <X size={18} strokeWidth={1.5} /> : <Menu size={18} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      {open && (
        <div id="mobile-menu" data-testid="mobile-menu" className="mobile-menu-enter border-t border-[#1C2A3A] lg:hidden">
          <ul className="cl-container flex flex-col py-4">
            {NAV_LINKS.map(({ id, label }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={go(id)}
                  data-testid={`mobile-nav-link-${id}`}
                  className={`flex items-center justify-between border-b border-[#1C2A3A]/60 py-4 text-xl font-light tracking-[-0.01em] ${
                    active === id ? "text-[#E7C99D]" : "text-[#E6EDF4]"
                  }`}
                >
                  {label}
                </a>
              </li>
            ))}
            <li className="pt-6 pb-2">
              <a
                href="#launching-soon"
                onClick={go("launching-soon")}
                data-testid="mobile-nav-get-started-button"
                className="btn-gold w-full"
              >
                Get Started <ArrowRight size={15} strokeWidth={1.4} />
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};
